import { MouseEventHandler, useMemo, useState } from 'react'
import { Position } from 'react-rnd'
import { useNavigate } from 'react-router-dom'
import { t } from 'i18next'
import { NestedMenuItem } from 'mui-nested-menu'
import { NavOptionType } from 'src/types/menu'
import { v4 as uuid } from 'uuid'
import nestedArrow from '@assets/images/nested-arrow.svg'
import { Box, ClickAwayListener, ListItemIcon, Menu, SxProps, Theme } from '@mui/material'

import { CustomSvgIcon } from '@components/CustomSvgIcon'
import { ICONS } from '@components/IconPicker/constants'
import { useAlignmentsNavList } from '@components/UserNavList/hooks'

import { useExecuteCommandMutation } from '@redux/api'
import { useFetchParameterPropertiesQuery } from '@redux/api/parameters.api'
import {
  openFileUploadDialog,
  openFormDialog,
  openSearchAssistantDialog,
  openSectionalWindowDialog,
  openViewDialog,
  setDialogPending,
} from '@redux/reducers/dialogWindowManager.reducer'
import { addFormHistory } from '@redux/reducers/formHistory.reducer'

import { useAppDispatch, useAppSelector } from '@hooks'
import {
  checkMaxOpenedWindowsCount,
  getCommandRequestData,
  getInitialPositionWindow,
  getOptionRoute,
  transformCommonParametersToValues,
} from '@helpers'
import { COMMON_PARAMETERS, ELEMENT_TYPE, ENTITY, FORM_TYPE, MENU_TYPE, ROUTES } from '@constants'

import { DIALOG_WINDOW_TYPE, DialogWindowType, ViewDialogWindowMeta } from '../../../src/types'

import { ListItem, ListItemButton } from './components/ListItem'
import { containsOption } from './helpers'

const menuSx: SxProps<Theme> = {
  '& .MuiList-root': {
    py: 0,
    top: -1,
  },
  '& .MuiPaper-root': {
    // borderRadius: 0,
    border: theme => theme.palette.border.field,
    boxShadow: theme => `2px 2px 6px 0px ${theme.palette.common.black}`,
    minWidth: 200,
    py: '4px',
  },
}

const nestedItemSx: SxProps<Theme> = {
  '& .MuiTypography-root': {
    p: '0px 12px',
    fontFamily: 'Montserrat Regular',
  },
  p: 0,
  pr: 3,
  '&:hover': {
    bgcolor: theme => theme.palette.background.hovered,
  },
}

type UserNavListProps = {
  option: NavOptionType
  itemsLine?: NavOptionType[] | undefined
  onCloseParentMenu?: () => void
  isParentMenuOpen?: boolean
  isChild?: boolean
  isOptionTreeReversed?: boolean
}

export const UserNavList = ({
  option,
  itemsLine,
  isParentMenuOpen,
  isChild,
  onCloseParentMenu,
  isOptionTreeReversed,
}: UserNavListProps) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isMenuOpen = useMemo(() => Boolean(anchorEl), [anchorEl])
  const hasOptions = Boolean(option.options && option.options?.length)
  const isChildWithOptions = Boolean(isChild && hasOptions)
  const { selectedId, dialogWindows, dialogsPending } = useAppSelector(
    state => state.dialogWindowManager
  )
  const selectedOption = dialogWindows.find(
    ({ id, type }) => id === selectedId && type === DIALOG_WINDOW_TYPE.VIEW
  ) as DialogWindowType<ViewDialogWindowMeta> | undefined
  const isSelected = isMenuOpen || (selectedOption && containsOption(option, selectedOption))
  const [isPopupOriginReversed, setIsPopupOriginReversed] = useState(isOptionTreeReversed)

  const { data: commonParameters } = useFetchParameterPropertiesQuery({
    code: COMMON_PARAMETERS.STATE,
  })

  const [executeCommand] = useExecuteCommandMutation()

  const isOptionLoading = dialogsPending[String(option.id)] || false
  const { order, margin } = useAlignmentsNavList(option, itemsLine)

  const initialCommonParameters = useMemo(
    () => transformCommonParametersToValues(commonParameters),
    [commonParameters]
  )

  const handleCloseMenu = () => setAnchorEl(null)

  const openSearchAssistant = (position?: Position | null) => {
    dispatch(
      openSearchAssistantDialog({
        id: uuid(),
        parentDialogId: null,
        type: DIALOG_WINDOW_TYPE.SEARCH_ASSISTANT,
        title: t('searchAssistant.title'),
        initialPosition: position,
        meta: {},
      })
    )
  }

  const openView = (position?: Position | null) => {
    if (checkMaxOpenedWindowsCount(initialCommonParameters)) {
      return
    }

    if (option.elementId && option.elementCode && option.elementTitle) {
      dispatch(
        openViewDialog({
          id: uuid(),
          parentDialogId: null,
          type: DIALOG_WINDOW_TYPE.VIEW,
          title: option.elementTitle,
          meta: {
            viewId: +option.elementId,
            viewCode: option.elementCode,
            title: option.elementTitle,
            path: option.id.toString(),
            menuId: option.id,
            ...(option.parentId && { parentId: option.parentId }),
          },
          initialPosition: position,
        })
      )
    }
  }

  const openForm = (position?: Position | null) => {
    if (checkMaxOpenedWindowsCount(initialCommonParameters)) return

    if (option.elementId && option.elementCode) {
      const id = uuid()
      dispatch(
        openFormDialog({
          id,
          parentDialogId: null,
          type: DIALOG_WINDOW_TYPE.FORM,
          title: option?.elementTitle ?? null,
          meta: {
            objectCode: '',
            formCode: option.elementCode.toString(),
            viewCode: '',
            path: option.id.toString(),
            event: FORM_TYPE.CREATE,
            globalId: id,
            entityType: ENTITY.VIEW,
            viewId: option.elementId,
            isIndependentForm: true,
          },
          initialPosition: position,
        })
      )

      dispatch(
        addFormHistory({
          id,
          form: {
            formCode: option.elementCode.toString(),
            objectCode: '',
            event: FORM_TYPE.CREATE,
            objectId: null,
            title: option?.elementTitle || '',
            id,
            isWindow: true,
          },
        })
      )
    }
  }

  const openSectionalWindow = (position?: Position | null) => {
    if (checkMaxOpenedWindowsCount(initialCommonParameters)) return

    if (option.elementId) {
      const id = uuid()

      dispatch(
        openSectionalWindowDialog({
          id,
          parentDialogId: null,
          type: DIALOG_WINDOW_TYPE.SECTIONAL_WINDOW,
          title: option.elementTitle ?? '',
          initialPosition: position,
          meta: {
            id: option.elementId,
          },
        })
      )
    }
  }

  const handleOpenFileUploadDialog = (position?: Position | null) => {
    if (checkMaxOpenedWindowsCount(initialCommonParameters)) return
    const id = uuid()
    dispatch(
      openFileUploadDialog({
        id,
        parentDialogId: null,
        type: DIALOG_WINDOW_TYPE.FILE_UPLOAD,
        title: option?.title || '',
        meta: {},
        initialPosition: position,
      })
    )
  }

  const handleClickItem: MouseEventHandler<HTMLDivElement> = event => {
    if (option.options?.length) {
      if (anchorEl) return

      setAnchorEl(event.currentTarget)

      const rect = event.currentTarget.getBoundingClientRect()
      const distanceToRightEdge = window.innerWidth - rect.right

      if (distanceToRightEdge < 400) {
        setIsPopupOriginReversed(true)
      } else {
        setIsPopupOriginReversed(false)
      }

      return
    }

    const initialPosition = getInitialPositionWindow(event)

    switch (option.elementType) {
      case ELEMENT_TYPE.SEARCH_ASSISTANT:
        openSearchAssistant(initialPosition)
        onCloseParentMenu?.()

        return
      case ELEMENT_TYPE.VIEW:
        if (option.background) {
          navigate(getOptionRoute(option))
        } else {
          openView(initialPosition)
        }

        onCloseParentMenu?.()

        return
      case ELEMENT_TYPE.GANTT_CHART:
        navigate(getOptionRoute(option))
        onCloseParentMenu?.()

        return

      case ELEMENT_TYPE.FORM:
        openForm(initialPosition)
        onCloseParentMenu?.()

        return

      case ELEMENT_TYPE.COMMAND:
        dispatch(setDialogPending({ id: String(option.id), isLoading: true }))

        executeCommand({
          method: option?.command?.httpMethod as string,
          objectCode: option?.command?.objectCode as string,
          commandCode: option?.command?.name as string,
          body: getCommandRequestData(option?.commandBody ?? [], {}),
          params: getCommandRequestData(option?.commandParameters ?? [], {}),
        }).finally(() => dispatch(setDialogPending({ id: String(option.id), isLoading: false })))

        return

      case ELEMENT_TYPE.FILE_UPLOAD:
        handleOpenFileUploadDialog(initialPosition)
        onCloseParentMenu?.()

        return

      case ELEMENT_TYPE.BAR_CHART:
        navigate(ROUTES.DIAGRAMS + `/${option.value}`)
        onCloseParentMenu?.()

        return
      case ELEMENT_TYPE.TIME_TABLE:
        navigate(ROUTES.TIME_LINE_GRID + `/${option.value}`)
        onCloseParentMenu?.()

        return
      case ELEMENT_TYPE.MAP_CONFIG:
        navigate(ROUTES.MAPS + `/${option.value}`)
        onCloseParentMenu?.()

        return

      case ELEMENT_TYPE.WINDOW:
        openSectionalWindow(initialPosition)
        onCloseParentMenu?.()

        return
      default:
        return
    }
  }

  return (
    <Box
      sx={{
        whiteSpace: 'nowrap',
        p: 0,
        textDecoration: 'none',
        color: theme => theme.palette.common.black,
        ...(!isChild && { maxWidth: 256 }),
        marginLeft: margin?.includes('ml') ? 'auto' : undefined,
        marginRight: margin?.includes('mr') ? 'auto' : undefined,
        order,
      }}
    >
      {isChildWithOptions ? (
        <NestedMenuItem
          component={ListItemButton}
          label={option.title}
          parentMenuOpen={Boolean(isParentMenuOpen)}
          selected={isSelected}
          sx={nestedItemSx}
          MenuProps={{
            anchorReference: 'anchorEl',
            sx: menuSx,
            transformOrigin: {
              horizontal: isPopupOriginReversed ? 'right' : 'left',
              vertical: 'top',
            },
            anchorOrigin: {
              horizontal: isPopupOriginReversed ? 'left' : 'right',
              vertical: 'top',
            },
          }}
          leftIcon={
            option.iconName && option.menuType === MENU_TYPE.ICON ? (
              <CustomSvgIcon
                imgStyle={{ maxHeight: 26, maxWidth: 38 }}
                src={ICONS[option.iconName].src}
                sx={{ ml: '11px', display: 'flex', height: 26 }}
              />
            ) : (
              <ListItemIcon sx={{ width: 20, height: 20, minWidth: 0 }} />
            )
          }
          rightIcon={
            <CustomSvgIcon
              fontSize='small'
              src={nestedArrow}
              sx={{ alignSelf: 'baseline', height: '1.2em', '& > img': { width: 15, height: 15 } }}
            />
          }
        >
          {option.options?.map(child => (
            <UserNavList
              key={child.id}
              isChild
              isOptionTreeReversed={isPopupOriginReversed}
              isParentMenuOpen={Boolean(isParentMenuOpen)}
              option={child}
              onCloseParentMenu={onCloseParentMenu}
            />
          ))}
        </NestedMenuItem>
      ) : (
        <ListItem
          key={option.id}
          disabled={isOptionLoading}
          icon={option.iconName ? ICONS[option.iconName].src : undefined}
          isChild={isChild}
          loading={isOptionLoading}
          option={option}
          selected={isSelected}
          onClick={handleClickItem}
        />
      )}
      {hasOptions ? (
        <ClickAwayListener mouseEvent='onMouseDown' onClickAway={handleCloseMenu}>
          <Menu
            disableAutoFocus
            disableEnforceFocus
            anchorEl={anchorEl}
            anchorReference='anchorEl'
            open={isMenuOpen}
            sx={menuSx}
            slotProps={{
              root: {
                style: { pointerEvents: 'none' },
              },
              paper: {
                style: { pointerEvents: 'auto' },
              },
            }}
            onClose={handleCloseMenu}
          >
            {option.options?.map(child => (
              <UserNavList
                key={child.id}
                isChild
                isOptionTreeReversed={isPopupOriginReversed}
                isParentMenuOpen={isMenuOpen}
                option={child}
                onCloseParentMenu={handleCloseMenu}
              />
            ))}
          </Menu>
        </ClickAwayListener>
      ) : null}
    </Box>
  )
}
